import { Spinner } from '@forwardflow/ui'
import { useLocation } from 'react-router-dom'
import {
   BarChart,
   Bar,
   XAxis,
   Cell,
   LabelList,
   ResponsiveContainer,
} from 'recharts'

export const SpeedToLeadGraph: React.FC<{
   sliderValue?: number | undefined
   speedToLead?: any
   isOnboarding?: boolean | undefined
   isStatsLoading: boolean
}> = ({
   sliderValue = 0,
   speedToLead,
   isOnboarding = false,
   isStatsLoading,
}) => {
   const location = useLocation()
   const isWaitingTime = location.pathname.includes('/waiting-time')

   const defaultData = [
      {
         responseTime: 'Immediate',
         conversionRate: 280,
      },
      {
         responseTime: '1 Min',
         conversionRate: 280,
      },
      {
         responseTime: '2 Min',
         conversionRate: 160,
      },
      {
         responseTime: '3 Min',
         conversionRate: 98,
      },
      {
         responseTime: '5 Min',
         conversionRate: 82,
      },
      {
         responseTime: '10 Min',
         conversionRate: 75,
      },
      {
         responseTime: '30 Min',
         conversionRate: 62,
      },
      {
         responseTime: '1 Hr',
         conversionRate: 36,
      },
      {
         responseTime: '5 Hr',
         conversionRate: 24,
      },
   ]

   const data =
      Array.isArray(speedToLead) && speedToLead.length > 0
         ? speedToLead
         : defaultData

   const displayData = data.slice(1)

   const firstValue = data[0]?.conversionRate || 280
   const selectedValue = data[sliderValue - 1]?.conversionRate || 24
   const difference = firstValue - selectedValue

   const renderCustomLabel = ({ x, y, width, value, index }: any) => {
      const radius = 10
      if (value === 0) return null
      const isHighlighted = index + 1 === sliderValue
      return (
         <g>
            <text
               x={x + width / 2}
               y={y + radius + 5}
               dy={0}
               fill={isHighlighted ? '#fff' : '#C4C4C4'}
               textAnchor="middle"
               dominantBaseline="middle"
               fontSize={12}
            >
               {value} %
            </text>
         </g>
      )
   }

   return (
      <div
         className={`bg-primary/10 ${
            !isWaitingTime ? 'border border-white/10 rounded-[20px]' : ''
         }  overflow-hidden max-w-[700px] flex-1`}
      >
         <div className="flex items-center justify-between gap-5 px-8 py-5 font-medium border-b border-white/10">
            <div className="">Speed to lead</div>
            {isStatsLoading ? (
               ''
            ) : (
               <div className="text-[12px]  max-w-64 text-right">
                  Your average speed to lead is{' '}
                  {data[sliderValue - 1]?.responseTime === 'Immediate'
                     ? 'immediate'
                     : `around ${
                          data[sliderValue - 1]?.responseTime || '1 Min'
                       }`}
               </div>
            )}
         </div>
         <div className="flex flex-col items-center justify-between gap-5 p-3 font-medium">
            <div
               className={`flex flex-col justify-between w-full px-3 pt-6  ${
                  isOnboarding ? 'h-[260px]' : 'h-[294px] 3xl:h-[310px]'
               }`}
            >
               <ResponsiveContainer width={'100%'} height={`100%`}>
                  {isStatsLoading ? (
                     <div className="flex items-center justify-center w-full h-full">
                        <Spinner />
                     </div>
                  ) : (
                     <BarChart data={displayData}>
                        <XAxis
                           className="text-[1rem]"
                           dataKey="responseTime"
                           dy={5}
                           dx={-7}
                           tick={{ fill: '#C4C4C4' }}
                           strokeWidth={0}
                           fontSize={12}
                        />
                        <Bar dataKey="conversionRate" radius={0}>
                           <LabelList
                              dataKey="conversionRate"
                              content={renderCustomLabel}
                           />
                           {displayData.map((_entry: any, index: number) => (
                              <Cell
                                 width={46}
                                 key={`cell-${index}`}
                                 fill={
                                    index + 2 === sliderValue
                                       ? sliderValue >= 7
                                          ? '#F00'
                                          : '#9061DA'
                                       : '#fff'
                                 }
                                 opacity={index + 2 === sliderValue ? 0.5 : 0.2}
                              />
                           ))}
                        </Bar>
                        {/* 
                        <ReferenceLine
                           label={{
                              position: 'insideBottomRight',
                              value: `${selectedValue}%`,
                              fill: sliderValue >= 6 ? '#FF5185' : '#9061DA',
                              fontSize: 12,
                           }}
                           y={selectedValue}
                           stroke={sliderValue >= 6 ? '#FF5185' : '#9061DA'}
                           strokeWidth={1}
                           opacity={0.5}
                           strokeDasharray="3 3"
                        /> */}
                        {/* {sliderValue > 1 && (
                           <g>
                              <rect
                                 x="70%"
                                 y={25}
                                 width="25%"
                                 height="25"
                                 rx="5"
                                 ry="5"
                                 fill="#9061DA"
                                 opacity="0.3"
                              />
                              <text
                                 x="83%"
                                 y={42}
                                 textAnchor="middle"
                                 fill="#FFFFFF"
                                 fontSize={12}
                                 fontWeight="bold"
                              >
                                 {`Difference: ${difference}% `}
                              </text>
                           </g>
                        )} */}

                        {/* <ReferenceLine
                           label={{
                              position: 'insideTopRight',
                              value: `${firstValue}%`,
                              fill: '#9061DA',
                              fontSize: 12,
                           }}
                           y={firstValue}
                           stroke="#9061DA"
                           strokeWidth={1}
                           opacity={0.5}
                           strokeDasharray="3 3"
                        /> */}
                     </BarChart>
                  )}
               </ResponsiveContainer>
            </div>
            {isStatsLoading ? (
               ''
            ) : (
               <div
                  className={`p-3 bg-primary/30 font-medium rounded-lg text-[12px] w-full ${
                     sliderValue >= 6 ? 'bg-[#FF5185]' : ''
                  }`}
               >
                  <div className="px-3">
                     {sliderValue > 1 ? (
                        <>
                           Fact: Your lead conversions will be{' '}
                           {difference || 280}% higher if a lead is contacted
                           within a minute of inquiry
                        </>
                     ) : (
                        <>
                           Your lead will be contacted immediately upon
                           creation, ensuring maximum conversion potential.
                        </>
                     )}
                  </div>
                  {isOnboarding && (
                     <div className="px-5 py-3 mt-2 rounded-lg bg-primary/30">
                        Forward Flow ensures your new leads will be contacted
                        within a minute after discovery
                     </div>
                  )}
               </div>
            )}
         </div>
      </div>
   )
}
